import Phaser from 'phaser';
import { DPR } from '@constants/common.const';
import { ModalSheetGeometry } from './ModalSheetGeometry';
import type { ModalSheetState } from './modal-sheet.types';

export class ModalSheetDragHandler<TemplateData> {
  private dragStartPointerY = 0;
  private dragStartY = 0;
  private dragging = false;
  private dragZone?: Phaser.GameObjects.Rectangle;

  constructor(
    private scene: Phaser.Scene,
    private geometry: ModalSheetGeometry<TemplateData>,
    private container: Phaser.GameObjects.Container,
    private onSnapComplete: (target: ModalSheetState) => void,
    private onDragUpdate: () => void,
  ) {}

  public setupDragZone(): Phaser.GameObjects.Rectangle {
    const dragZoneHeight = 64 * DPR;
    this.dragZone = this.scene.add
      .rectangle(0, 0, this.geometry.sheetWidth, dragZoneHeight, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive({ draggable: true, useHandCursor: true });

    this.dragZone.on('dragstart', (pointer: Phaser.Input.Pointer) => {
      this.handleDragStart(pointer);
    });

    this.dragZone.on('drag', (pointer: Phaser.Input.Pointer) => {
      this.handleDrag(pointer);
    });

    this.dragZone.on('dragend', () => {
      this.handleDragEnd();
    });

    return this.dragZone;
  }

  private handleDragStart(pointer: Phaser.Input.Pointer): void {
    this.dragging = true;
    this.dragStartPointerY = pointer.y;
    this.dragStartY = this.container.y;
  }

  private handleDrag(pointer: Phaser.Input.Pointer): void {
    if (!this.dragging) return;
    const dy = pointer.y - this.dragStartPointerY;
    this.container.y = Phaser.Math.Clamp(
      this.dragStartY + dy,
      this.geometry.expandedY,
      this.geometry.hiddenY,
    );
    this.onDragUpdate();
  }

  private handleDragEnd(): void {
    this.dragging = false;
    this.snapToNearest();
  }

  private snapToNearest(): void {
    const mid = (this.geometry.collapsedY + this.geometry.expandedY) / 2;
    // 10% of the way down from collapsed to hidden
    const hideThreshold = Phaser.Math.Linear(
      this.geometry.collapsedY,
      this.geometry.hiddenY,
      0.1,
    );

    const target: ModalSheetState =
      this.container.y <= mid
        ? 'expanded'
        : this.container.y >= hideThreshold
          ? 'hidden'
          : 'collapsed';

    this.onSnapComplete(target);
  }

  public destroy(): void {
    if (this.dragZone) {
      this.dragZone.off('dragstart');
      this.dragZone.off('drag');
      this.dragZone.off('dragend');
      this.dragZone = undefined;
    }
  }
}
